import React from "react";
import { Anchor, CheckCircle2, XCircle } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const serviceRows = [
  { label: "Fuel", terms: ["fuel", "diesel", "petrol", "gas"] },
  { label: "Water", terms: ["water", "fresh water"] },
  { label: "Shore power", terms: ["shore power", "electricity", "electric", "power", "220v", "30a", "50a"] },
  { label: "Wi-Fi", terms: ["wifi", "wi-fi", "internet"] },
  { label: "Showers", terms: ["shower", "showers"] },
  { label: "Laundry", terms: ["laundry", "washing"] },
  { label: "Restaurant", terms: ["restaurant", "bar", "cafe", "food"] },
  { label: "Repairs", terms: ["repair", "maintenance", "mechanic", "yard", "service"] },
  { label: "Visitor berths", custom: (marina) => Number(marina.available_berths || marina.total_berths || 0) > 0 },
];

const detailRows = [
  { label: "Location", get: (m) => [m.city, m.region, m.country].filter(Boolean).join(", ") || m.location || "Not listed" },
  { label: "Price/night", get: (m) => Number(m.price_per_night || 0) > 0 ? `${m.currency || "EUR"} ${m.price_per_night}` : "Not listed" },
  { label: "Rating", get: (m) => Number(m.rating || 0) > 0 ? `${Number(m.rating).toFixed(1)}/5` : "Not listed" },
  { label: "Total berths", get: (m) => m.total_berths || "Not listed" },
  { label: "Available berths", get: (m) => m.available_berths || "Not listed" },
  { label: "Max vessel length", get: (m) => m.max_vessel_length_m ? `${m.max_vessel_length_m} m` : "Not listed" },
  { label: "Max draft", get: (m) => m.max_draft_m ? `${m.max_draft_m} m` : "Not listed" },
  { label: "Mooring types", get: (m) => formatList(m.mooring_types) },
  { label: "VHF", get: (m) => m.vhf_channel || "Not listed" },
  { label: "Phone", get: (m) => m.phone || "Not listed" },
];

function formatList(items = []) {
  return items.length ? items.join(", ") : "Not listed";
}

function hasService(marina, row) {
  if (row.custom) return row.custom(marina);
  const text = [...(marina.facilities || []), ...(marina.mooring_types || []), marina.description].filter(Boolean).join(" ").toLowerCase();
  return row.terms.some((term) => text.includes(term));
}

function ServiceCell({ value }) {
  return value ? (
    <span className="inline-flex items-center gap-2 font-bold text-emerald-700"><CheckCircle2 className="h-4 w-4" /> Yes</span>
  ) : (
    <span className="inline-flex items-center gap-2 text-muted-foreground"><XCircle className="h-4 w-4" /> Not listed</span>
  );
}

export default function MarinaComparisonPanel({ marinas = [] }) {
  const choices = React.useMemo(() => marinas.filter((marina) => marina?.id && marina?.name).slice(0, 300), [marinas]);
  const [leftId, setLeftId] = React.useState("");
  const [rightId, setRightId] = React.useState("");

  React.useEffect(() => {
    if (!choices.length) return;
    const nextLeftId = choices.some((marina) => marina.id === leftId) ? leftId : choices[0]?.id || "";
    const nextRightId = choices.some((marina) => marina.id === rightId && marina.id !== nextLeftId)
      ? rightId
      : choices.find((marina) => marina.id !== nextLeftId)?.id || "";
    if (leftId !== nextLeftId) setLeftId(nextLeftId);
    if (rightId !== nextRightId) setRightId(nextRightId);
  }, [choices, leftId, rightId]);

  const left = choices.find((marina) => marina.id === leftId);
  const right = choices.find((marina) => marina.id === rightId);
  if (choices.length < 2) return null;

  return (
    <Card className="overflow-hidden border-primary/20 bg-card/95 shadow-xl">
      <CardContent className="p-5 md:p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <Badge className="bg-primary/10 text-primary shadow-none"><Anchor className="mr-1 h-3.5 w-3.5" /> Marina comparison</Badge>
            <h2 className="mt-3 text-2xl font-black text-foreground">Compare two marinas side by side</h2>
            <p className="mt-2 text-sm text-muted-foreground">Choose two different marinas from the current results and compare amenities, services, and marina details.</p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:min-w-[34rem]">
            <select value={leftId} onChange={(event) => setLeftId(event.target.value)} className="h-12 rounded-2xl border border-border bg-background px-4 text-sm font-bold text-foreground">
              {choices.map((marina) => <option key={marina.id} value={marina.id} disabled={marina.id === rightId}>{marina.name}</option>)}
            </select>
            <select value={rightId} onChange={(event) => setRightId(event.target.value)} className="h-12 rounded-2xl border border-border bg-background px-4 text-sm font-bold text-foreground">
              {choices.map((marina) => <option key={marina.id} value={marina.id} disabled={marina.id === leftId}>{marina.name}</option>)}
            </select>
          </div>
        </div>

        {left && right && (
          <div className="mt-5 overflow-x-auto rounded-3xl border border-border">
            <table className="min-w-[720px] text-left text-sm" data-keep-table>
              <thead className="bg-muted/70 text-foreground"><tr><th className="w-56 p-4 font-black">Amenity / service</th><th className="p-4 font-black">{left.name}</th><th className="p-4 font-black">{right.name}</th></tr></thead>
              <tbody className="divide-y divide-border bg-card">
                {detailRows.map((row) => <tr key={row.label}><td className="p-4 font-bold text-muted-foreground">{row.label}</td><td className="p-4 font-semibold text-foreground">{row.get(left)}</td><td className="p-4 font-semibold text-foreground">{row.get(right)}</td></tr>)}
                {serviceRows.map((row) => <tr key={row.label}><td className="p-4 font-bold text-muted-foreground">{row.label}</td><td className="p-4"><ServiceCell value={hasService(left, row)} /></td><td className="p-4"><ServiceCell value={hasService(right, row)} /></td></tr>)}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}